import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const { 
  FiUpload, FiDownload, FiSettings, FiTrash2, FiImage, FiCheck, 
  FiX, FiLoader, FiZap, FiChevronDown, FiPackage, FiFile 
} = FiIcons;

function BatchImageResizer() {
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState({
    width: 800,
    height: 600,
    quality: 90,
    format: 'original',
    maintainAspectRatio: true,
    resizeMode: 'fit'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCreatingZip, setIsCreatingZip] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((files) => {
    const newImages = Array.from(files)
      .filter(file => file.type.startsWith('image/'))
      .map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        preview: URL.createObjectURL(file),
        status: 'pending',
        processedBlob: null,
        originalDimensions: null
      }));

    // Get original dimensions for each image
    newImages.forEach(imageData => {
      const img = new Image();
      img.onload = () => {
        imageData.originalDimensions = { width: img.width, height: img.height };
        setImages(prev => [...prev]);
      };
      img.src = imageData.preview;
    });

    setImages(prev => [...prev, ...newImages]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const removeImage = (id) => {
    setImages(prev => {
      const imageToRemove = prev.find(img => img.id === id);
      if (imageToRemove?.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      if (imageToRemove?.processedBlob) {
        URL.revokeObjectURL(imageToRemove.processedBlob);
      }
      return prev.filter(img => img.id !== id);
    });
  };

  const clearAll = () => {
    images.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
      if (img.processedBlob) URL.revokeObjectURL(img.processedBlob);
    });
    setImages([]);
  };

  const resizeImage = (imageData) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = settings;
        const originalWidth = img.width;
        const originalHeight = img.height;

        if (settings.maintainAspectRatio) {
          const aspectRatio = originalWidth / originalHeight;
          
          if (settings.resizeMode === 'fit') {
            if (width / height > aspectRatio) {
              width = height * aspectRatio;
            } else {
              height = width / aspectRatio;
            }
          } else if (settings.resizeMode === 'fill') {
            if (width / height < aspectRatio) {
              width = height * aspectRatio;
            } else {
              height = width / aspectRatio;
            }
          }
        }

        canvas.width = width;
        canvas.height = height;

        // High-quality resize
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const outputFormat = settings.format === 'original' 
          ? imageData.file.type 
          : `image/${settings.format}`;

        canvas.toBlob((blob) => {
          resolve(blob);
        }, outputFormat, settings.quality / 100);
      };

      img.src = imageData.preview;
    });
  };

  const processAllImages = async () => {
    setIsProcessing(true);
    
    for (let i = 0; i < images.length; i++) {
      const imageData = images[i];
      
      setImages(prev => prev.map(img => 
        img.id === imageData.id 
          ? { ...img, status: 'processing' }
          : img
      ));

      try {
        const processedBlob = await resizeImage(imageData);
        
        setImages(prev => prev.map(img => 
          img.id === imageData.id 
            ? { 
                ...img, 
                status: 'completed', 
                processedBlob,
                processedSize: processedBlob.size
              }
            : img
        ));
      } catch (error) {
        setImages(prev => prev.map(img => 
          img.id === imageData.id 
            ? { ...img, status: 'error' }
            : img
        ));
      }
    }
    
    setIsProcessing(false);
  };

  const downloadAsZip = async () => {
    const completedImages = images.filter(img => img.status === 'completed' && img.processedBlob);
    
    if (completedImages.length === 0) {
      alert('No processed images to download');
      return;
    }

    setIsCreatingZip(true);
    
    try {
      const zip = new JSZip();
      const folder = zip.folder("resized_images");

      // Add each processed image to the ZIP
      for (const imageData of completedImages) {
        const extension = settings.format === 'original' 
          ? imageData.name.split('.').pop()
          : settings.format === 'jpeg' ? 'jpg' : settings.format;
        const baseName = imageData.name.replace(/\.[^/.]+$/, '');
        const fileName = `${baseName}_resized.${extension}`;
        
        folder.file(fileName, imageData.processedBlob);
      }

      // Generate and download the ZIP file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      saveAs(zipBlob, `resized_images_${timestamp}.zip`);
      
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      alert('Error creating ZIP file. Please try downloading individually.');
    }
    
    setIsCreatingZip(false);
  };

  const downloadIndividually = () => {
    const completedImages = images.filter(img => img.status === 'completed' && img.processedBlob);
    
    completedImages.forEach((imageData, index) => {
      setTimeout(() => {
        const url = URL.createObjectURL(imageData.processedBlob);
        const a = document.createElement('a');
        a.href = url;
        const extension = settings.format === 'original' 
          ? imageData.name.split('.').pop()
          : settings.format === 'jpeg' ? 'jpg' : settings.format;
        const baseName = imageData.name.replace(/\.[^/.]+$/, '');
        a.download = `${baseName}_resized.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, index * 100); // Small delay between downloads to avoid browser blocking
    });
  };

  const downloadSingle = (imageData) => {
    if (imageData.processedBlob) {
      const url = URL.createObjectURL(imageData.processedBlob);
      const a = document.createElement('a');
      a.href = url;
      const extension = settings.format === 'original' 
        ? imageData.name.split('.').pop()
        : settings.format === 'jpeg' ? 'jpg' : settings.format;
      const baseName = imageData.name.replace(/\.[^/.]+$/, '');
      a.download = `${baseName}_resized.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing': return FiLoader;
      case 'completed': return FiCheck;
      case 'error': return FiX;
      default: return FiImage;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'text-blue-600 animate-spin';
      case 'completed': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-400';
    }
  };

  const completedImages = images.filter(img => img.status === 'completed');
  const totalOriginalSize = images.reduce((sum, img) => sum + img.size, 0);
  const totalProcessedSize = completedImages.reduce((sum, img) => sum + (img.processedSize || 0), 0);
  const compressionRatio = totalOriginalSize > 0 ? ((totalOriginalSize - totalProcessedSize) / totalOriginalSize * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">Batch Image Resizer</h2>
          <p className="text-blue-100">Resize multiple images at once with professional quality</p>
        </div>

        <div className="p-6">
          {/* Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-500 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Drop images here or click to select
            </h3>
            <p className="text-gray-500 mb-4">
              Support for JPEG, PNG, WebP, and GIF formats
            </p>
            <p className="text-sm text-gray-400">
              Maximum file size: 50MB per image
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
          </div>

          {/* Settings Panel */}
          <div className="mt-6">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center justify-between w-full bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-colors"
            >
              <div className="flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2 text-gray-600" />
                <span className="font-medium text-gray-700">Resize Settings</span>
              </div>
              <SafeIcon 
                icon={FiChevronDown} 
                className={`text-gray-400 transition-transform ${showSettings ? 'rotate-180' : ''}`} 
              />
            </button>

            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-gray-50 rounded-b-lg p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Width (px)
                      </label>
                      <input
                        type="number"
                        value={settings.width}
                        onChange={(e) => setSettings(prev => ({ ...prev, width: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="10000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Height (px)
                      </label>
                      <input
                        type="number"
                        value={settings.height}
                        onChange={(e) => setSettings(prev => ({ ...prev, height: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        min="1"
                        max="10000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quality ({settings.quality}%)
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="100"
                        value={settings.quality}
                        onChange={(e) => setSettings(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Output Format
                      </label>
                      <select
                        value={settings.format}
                        onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="original">Keep Original</option>
                        <option value="jpeg">JPEG</option>
                        <option value="png">PNG</option>
                        <option value="webp">WebP</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Resize Mode
                      </label>
                      <select
                        value={settings.resizeMode}
                        onChange={(e) => setSettings(prev => ({ ...prev, resizeMode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="fit">Fit (maintain aspect ratio)</option>
                        <option value="fill">Fill (crop if needed)</option>
                        <option value="stretch">Stretch (ignore aspect ratio)</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.maintainAspectRatio}
                          onChange={(e) => setSettings(prev => ({ ...prev, maintainAspectRatio: e.target.checked }))}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Maintain aspect ratio</span>
                      </label>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Images List */}
          {images.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  Images ({images.length})
                </h3>
                <div className="flex space-x-2">
                  {completedImages.length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowDownloadOptions(!showDownloadOptions)}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <SafeIcon icon={FiDownload} className="mr-2" />
                        Download Options
                        <SafeIcon 
                          icon={FiChevronDown} 
                          className={`ml-2 transition-transform ${showDownloadOptions ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      
                      <AnimatePresence>
                        {showDownloadOptions && (
                          <motion.div
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                          >
                            <div className="p-4">
                              <h4 className="font-semibold text-gray-900 mb-3">Choose Download Method</h4>
                              
                              {/* ZIP Download Option */}
                              <button
                                onClick={() => {
                                  downloadAsZip();
                                  setShowDownloadOptions(false);
                                }}
                                disabled={isCreatingZip}
                                className="w-full flex items-center p-3 mb-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200 disabled:opacity-50"
                              >
                                <div className="bg-blue-600 p-2 rounded-lg mr-3">
                                  <SafeIcon icon={isCreatingZip ? FiLoader : FiPackage} className={`text-white ${isCreatingZip ? 'animate-spin' : ''}`} />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-gray-900">
                                    {isCreatingZip ? 'Creating ZIP...' : 'Download as ZIP'}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Single file with all images ({completedImages.length} files)
                                  </div>
                                </div>
                              </button>

                              {/* Individual Download Option */}
                              <button
                                onClick={() => {
                                  downloadIndividually();
                                  setShowDownloadOptions(false);
                                }}
                                className="w-full flex items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200"
                              >
                                <div className="bg-green-600 p-2 rounded-lg mr-3">
                                  <SafeIcon icon={FiFile} className="text-white" />
                                </div>
                                <div className="text-left">
                                  <div className="font-medium text-gray-900">Download Individually</div>
                                  <div className="text-sm text-gray-600">
                                    Separate downloads for each image
                                  </div>
                                </div>
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                  <button
                    onClick={processAllImages}
                    disabled={isProcessing}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <SafeIcon icon={isProcessing ? FiLoader : FiZap} className={`mr-2 ${isProcessing ? 'animate-spin' : ''}`} />
                    {isProcessing ? 'Processing...' : 'Resize All'}
                  </button>
                  <button
                    onClick={clearAll}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="mr-2" />
                    Clear All
                  </button>
                </div>
              </div>

              {/* Download Options Info */}
              {completedImages.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <SafeIcon icon={FiDownload} className="text-blue-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-1">Download Options Available</h4>
                      <p className="text-sm text-blue-700">
                        <strong>ZIP Download:</strong> Get all {completedImages.length} images in a single compressed file (recommended for many images)<br/>
                        <strong>Individual Download:</strong> Download each image separately (may trigger multiple download prompts)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Statistics */}
              {completedImages.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-green-600">{completedImages.length}</div>
                      <div className="text-sm text-green-700">Images Processed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatFileSize(totalOriginalSize)} → {formatFileSize(totalProcessedSize)}
                      </div>
                      <div className="text-sm text-green-700">Total Size</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {compressionRatio > 0 ? '-' : '+'}{Math.abs(compressionRatio).toFixed(1)}%
                      </div>
                      <div className="text-sm text-green-700">Size Change</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {images.map((imageData) => (
                  <motion.div
                    key={imageData.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                  >
                    <img
                      src={imageData.preview}
                      alt={imageData.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {imageData.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(imageData.size)}
                        {imageData.originalDimensions && (
                          <span> • {imageData.originalDimensions.width}×{imageData.originalDimensions.height}</span>
                        )}
                        {imageData.processedSize && (
                          <span> → {formatFileSize(imageData.processedSize)}</span>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <SafeIcon 
                        icon={getStatusIcon(imageData.status)} 
                        className={`text-lg ${getStatusColor(imageData.status)}`} 
                      />
                      
                      {imageData.status === 'completed' && (
                        <button
                          onClick={() => downloadSingle(imageData)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Download this image"
                        >
                          <SafeIcon icon={FiDownload} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => removeImage(imageData.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BatchImageResizer;